import React from 'react';
import { parseJson } from '../../form_tools/extraFunction';
import {
  dynamicRender,
  hasMeaningfulOptions,
  normalizeConditionalProperties,
  resolveDynamicBoolean,
  resolveVisibility,
} from '../../form_tools/dynamicRender';
import { parsePlaceholder } from '../../utils/detectAnswer';

import CommentBubble from '../CommentBubble';

const isEmptyValue = (value) => {
  if (!value) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object') {
    if (value.text_answer === '' || value.text_answer === undefined)
      return true;
    if (value.number_answer === '' || value.number_answer === undefined)
      return true;
    if (
      value.choice_answer &&
      (!value.choice_answer.length || value.choice_answer[0] === '')
    )
      return true;
    if (value.calendar_answer === '' || value.calendar_answer === undefined)
      return true;
    if (value.answer && (!value.answer.length || value.answer[0] === ''))
      return true;
  }
  return false;
};

const parseAddressAuto = value => {
  if (value && typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      return {
        formatted: value,
        street_address: '',
        locality: '',
        region: '',
        postal_code: '',
        country: '',
      };
    }
  }
  return {
    formatted: '',
    street_address: '',
    locality: '',
    region: '',
    postal_code: '',
    country: '',
  };
};

const createAnswerData = (format, auto) => {

  switch (format) {
    case 'number':
    case 'number_input':
      return { number_answer: auto.toString() };
    case 'short_text':
    case 'single_line_text':
      return { text_answer: auto.toString() };
    case 'long_text':
    case 'free_text':
      return { text_answer: auto.toString() };
    case 'radio_button':
    case 'single_choice':
      return { choice_answer: [auto] };
    case 'checkbox':
    case 'multiple_choice':
      // Multiple choice expects answer format, not choice_answer
      return { answer: [auto] };
    case 'dropdown':
    case 'single_select':
      return { choice_answer: [auto] };
    case 'date':
    case 'calendar':
      return { calendar_answer: auto };
    case 'map':
      return { map_answer: auto };
    case 'list':
    case 'list_input':
      const listItems = Array.isArray(auto) ? auto : [auto];
      return {
        answer: listItems.map((item) =>
          typeof item === 'object' ? JSON.stringify(item) : String(item)
        ),
      };
    case 'signature':
    case 'esignature':
      return { esignature_answer: auto };
    case 'image_annotation':
      return { image_annotation_answer: auto };
    case 'address':
      return { address_answer: parseAddressAuto(auto) };
    default:
      return {
        answer: [
          typeof auto === 'object' ? JSON.stringify(auto) : String(auto),
        ],
      };
  }
};

const FormQuestionRenderer = ({
  pageData,
  form,
  formInstance,
  componentRenderer,
  readOnly,
  timezone,
  requiredList,
  requiredListTable,
  username,
  sended,
  pageProcessing,
  previewMode,
  onAnswerChange,
}) => {
  const renderRequiredErrors = (componentKey) => {
    const hasTableErrors = requiredListTable[componentKey];
    const hasFieldError = requiredList.includes(componentKey);

    return (
      <>
        {/* Table-specific required errors */}
        {hasTableErrors &&
          hasTableErrors.map((colName, index) => (
            <div key={index} className="descriptionImageDiv">
              <i className="material-icons descriptionImage">error</i>
              <p className="error">{colName} is a required field</p>
            </div>
          ))}

        {/* General required field error */}
        {hasFieldError && (
          <div className="descriptionImageDiv">
            <i className="material-icons descriptionImage">error</i>
            <p className="error">This question is required</p>
          </div>
        )}
      </>
    );
  };

  const renderQuestion = (component, questionList) => {
    const fieldType = component.type || component.views?.answer?.view?.format;
    if (fieldType === 'row_text' || fieldType === 'content_block') {
      return null;
    }

    const v2 = isV2Component(component);
    const description = v2
      ? (component.dynamicDescription
          ? dynamicRender(formInstance, component.description || '', component.dynamicDescription.Statement ? component.dynamicDescription : null)
          : (component.description || ''))
      : component.views?.question?.description?.default;

    const isRequired = resolveDynamicBoolean(formInstance, component.required);

    const questionFormat = component.views?.question?.view?.format || 'row_text';

    return (
      <div className="descriptionQuestionDiv" id="question-area">
        {componentRenderer.componentSelector(
          questionFormat,
          {
            data:
              (questionList || '') +
              `${isRequired ? ' * ' : ''}`,
            style: v2 ? undefined : parseJson(component.views?.question?.style),
          },
          'question',
          component.key
        )}

        <p
          id={`${component.key}-question-description`}
          className="descriptionQuestionPtag"
        >
          {parsePlaceholder(
            v2
              ? (description || '')
              : dynamicRender(
                  formInstance,
                  description,
                  component.views?.question?.description?.dynamic
                ),
            form
          )}
        </p>
      </div>
    );
  };

  const renderAnswer = (component, answerList, auto, readonly) => {
    const fieldType = component.type || component.views?.answer?.view?.format;
    const v2 = isV2Component(component);
    const questionText = v2
      ? (component.label !== undefined ? component.label : '')
      : (component.label || component.views?.question?.view?.data?.default);
    return (
      <div className="descriptionAnswerDiv" id="answer-area">
        <div>
          {(() => {
            const resolvedNumRows = (component.numRows ?? component.minRows ?? component.maxSections) || component.views?.answer?.view?.data?.rowLabels?.length || 3;
            const resolvedExportType = normalizeV2ExportType(component.exportType) || component.exportType || 'rowsAsLists';
            const resolvedData = fieldType === 'file_preview'
              ? (component.fileUrl || component.rte_answer_data_default || component.views?.answer?.view?.data?.default || answerList || '')
              : (answerList != null ? answerList : '');
            const resolvedRowLabels = (v2 && hasItems(component.rowLabels))
              ? component.rowLabels
              : component.views?.answer?.view?.data?.rowLabels;
            const resolvedTableCells = hasItems(component.columns)
              ? component.columns
              : component.tableCells;

            return componentRenderer.componentSelector(
              fieldType,
              {
                ...component,
                data: resolvedData,
                style: v2 ? undefined : parseJson(component.views?.answer?.style),
                key: component.key,
                comment: component.comment,
                list_min: component.minItems ?? component.list_min,
                list_max: component.maxItems ?? component.list_max,
                file_type: component.file_type ?? component.fileType,
                question_text: questionText,
                drop_down: component.drop_down ?? component.searchable,
                outputFormat: component.outputFormat,
                timezone: component.timezone,
                numRows: resolvedNumRows,
                exportType: resolvedExportType,
                columns: resolvedTableCells,
                tableCells: resolvedTableCells,
                validation: component.validation,
                ...(resolvedRowLabels ? { rowLabels: resolvedRowLabels } : {}),
                auto: auto,
                read_only: readOnly === true ? true : JSON.parse(readonly),
              },
              'answer',
              component.key
            );
          })()}

          <p className="descriptionAnswerPtag">
            {v2
              ? ''
              : dynamicRender(
                  formInstance,
                  component.views?.answer?.description?.default,
                  component.views?.answer?.description?.dynamic
                )}
          </p>
        </div>
      </div>
    );
  };

  const renderComments = (component) => {
    if (!resolveDynamicBoolean(formInstance, component.comment)) return null;

    return (
      <div
        style={{
          display: 'inline-block',
          flexDirection: 'column',
          width: '100%',
          marginTop: '1rem',
        }}
      >
        <h6 style={{ marginBottom: '1rem' }}>Comments</h6>

        {/* Legacy comment */}
        {form.answer_data[component.key] &&
          form.answer_data[component.key].comment && (
            <CommentBubble
              legacy
              comment={form.answer_data[component.key].comment}
            />
          )}

        {/* New comments */}
        {form.comments[component.key] &&
          form.comments[component.key].map(({ comment, user, date }) => (
            <CommentBubble
              key={`${component.key}-${date}`}
              comment={comment}
              user={user}
              date={date}
              timezone={timezone}
            />
          ))}

      </div>
    );
  };

  const handleAutoValue = (component, auto, answerList) => {
    if (!onAnswerChange) return;

    const format = component.type || component.views?.answer?.view?.format;
    const questionText = component.label || component.views?.question?.view?.data?.default;

    if (auto !== '') {
      const answer_data = createAnswerData(format, auto);
      onAnswerChange(
        answer_data,
        component.key,
        questionText,
        auto
      );
    }
  };

  const isV2Component = (comp) => comp.type && !comp.views;

  const normalizeV2Columns = (columns) => {
    if (!Array.isArray(columns)) return columns;
    return columns.map(col => ({
      ...col,
      columnName: col.columnName || col.label || '',
      id: col.id || col.key || '',
      question: col.question || col.label || '',
    }));
  };

  const normalizeV2ExportType = (exportType) => {
    if (exportType === 'columns_as_lists') return 'columnsAsLists';
    if (exportType === 'rows_as_lists') return 'rowsAsLists';
    return exportType;
  };

  const hasItems = (value) => Array.isArray(value) && value.length > 0;

  const getLegacyAnswerList = (component) =>
    parseJson(
      dynamicRender(
        formInstance,
        component.views?.answer?.view?.data?.default,
        component.views?.answer?.view?.data?.dynamic
      )
    );

  const processComponent = (component) => {
    const fieldType = component.type || component.views?.answer?.view?.format;
    component = normalizeConditionalProperties(component);
    const v2 = isV2Component(component);

    let questionText;
    let answerList;
    let questionList;

    if (v2) {
      questionText = component.label !== undefined ? component.label : '';
      if (fieldType === 'table' || fieldType === 'repeatable_section') {
        answerList = normalizeV2Columns(component.columns || component.fields || []);
      } else {
        answerList = component.options || '';
      }
      questionList = questionText;
    } else {
      questionText = component.label || component.views?.question?.view?.data?.default;
      if (fieldType === 'table' || fieldType === 'repeatable_section') {
        const v2Columns = normalizeV2Columns(component.columns || component.fields || []);
        answerList = hasItems(v2Columns) ? v2Columns : getLegacyAnswerList(component);
      } else if (hasMeaningfulOptions(component.options)) {
        answerList = component.options;
      } else {
        answerList = getLegacyAnswerList(component);
      }
      questionList = parseJson(
        dynamicRender(
          formInstance,
          questionText,
          component.views?.question?.view?.data?.dynamic
        )
      );
    }

    const visible = resolveVisibility(formInstance, component);

    let auto;
    if (component.auto !== undefined && component.auto !== null) {
      auto = dynamicRender(formInstance, '', component.auto);
    } else if (component.prefill !== undefined && component.prefill !== null) {
      auto = dynamicRender(formInstance, '', component.prefill);
    } else {
      auto = '';
    }
    if (auto !== '') {
      if (
        fieldType === 'multiple_choice' || fieldType === 'checkbox' ||
        fieldType === 'single_choice' || fieldType === 'radio_button' ||
        fieldType === 'single_select' || fieldType === 'dropdown'
      ) {
        // Validate that the auto value exists in the answer list
        const answer = formInstance.isContain
          ? formInstance.isContain(auto, answerList)
          : null;

        if (!answer) {
          // Clear invalid prefill value
          auto = '';
        } else {
          // For single_select, format auto as array [null, value]
          if (fieldType === 'single_select' || fieldType === 'dropdown') {
            // Extract the value from the answer array (answer[1] contains the display value)
            const value = Array.isArray(answer) ? answer[1] : answer;
            auto = [null, value];
          } else {
            // For multiple_choice and single_choice, use the matched answer
            auto = answer;
          }
        }
      } else if (typeof answerList !== 'object') {
        answerList = auto;
      }

      // Only call handleAutoValue for components that don't have their own auto handling
      // REMOVED: This was causing auto values to overwrite user input on every render
      // Components should handle their own auto values in componentDidMount/componentDidUpdate
    } else {
      // Only call handleAutoValue for components that don't have their own auto handling
      // REMOVED: This was causing auto values to overwrite user input on every render
      // Components should handle their own auto values in componentDidMount/componentDidUpdate
    }

    let readonly;
    if (component.read_only !== undefined && component.read_only !== null) {
      readonly = dynamicRender(formInstance, 'false', component.read_only) + '';
    } else if (component.readOnly !== undefined && component.readOnly !== null) {
      readonly = component.readOnly === true ? 'true' : 'false';
    } else {
      readonly = 'false';
    }
    const comment = resolveDynamicBoolean(formInstance, component.comment);
    component = { ...component, comment };

    // Check visibility - if hidden but has auto/prefilled values, set them before hiding
    if (visible === 'false' || visible === '') {
      // Handle prefilled values for hidden components
      if (
        auto !== '' &&
        onAnswerChange &&
        (!form.answer_data[component.key] ||
          isEmptyValue(form.answer_data[component.key]))
      ) {
        // Set the auto/prefilled value for hidden component
        const answer_data = (() => {
          switch (fieldType) {
            case 'number':  // v2
            case 'number_input':
              return { number_answer: auto.toString() };
            case 'short_text':  // v2
            case 'single_line_text':
              return { text_answer: auto.toString() };
            case 'long_text':  // v2
            case 'free_text':
              return { text_answer: auto.toString() };
            case 'radio_button':  // v2
            case 'single_choice':
            case 'checkbox':  // v2
            case 'multiple_choice':
            case 'dropdown':  // v2
            case 'single_select':
              return { choice_answer: [auto] };
            case 'date':  // v2
            case 'calendar':
              return { calendar_answer: auto };
            case 'address':
              return { address_answer: parseAddressAuto(auto) };
            default:
              return { answer: [auto] };
          }
        })();

        setTimeout(() => {
          onAnswerChange(
            answer_data,
            component.key,
            questionText,
            auto
          );
        }, 0);
      }
      return null;
    }

    return {
      component,
      answerList,
      questionList,
      auto,
      readonly,
      visible: true,
    };
  };

  const components = pageData?.components || pageData?.fields;
  if (!pageData || !components) {
    return null;
  }

  return (
    <>
      {components.map((component) => {
        const processedComponent = processComponent(component);

        // For components that should be hidden, still render the div but with hidden class
        const isVisible = processedComponent !== null;
        const { answerList, questionList, auto, readonly } =
          processedComponent || {};
        const renderComponent = processedComponent?.component || component;

        return (
          <div
            key={component.key}
            className={
              isVisible ? 'formComponentVisible' : 'formComponentHidden'
            }
          >
            {isVisible && (
              <>
                {/* Required field errors */}
                {renderRequiredErrors(renderComponent.key)}

                {/* Question section */}
                {renderQuestion(renderComponent, questionList)}

                {/* Answer section */}
                {renderAnswer(renderComponent, answerList, auto, readonly)}

                {/* Comments section */}
                {renderComments(renderComponent)}
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default FormQuestionRenderer;
